import { FC, useCallback, useState } from 'react';
import { useFragment, useMutation } from 'react-relay';
import { RangeKeyDict } from 'react-date-range';
import graphql from 'babel-plugin-relay/macro';
import format from 'date-fns/format';

import AddIcon from '@mui/icons-material/Add';
import Box from '@mui/material/Box';
import LoadingButton from '@mui/lab/LoadingButton';
import DateRangePickerBase from 'components/DateRangePickerBase';

import { DATE_FORMAT } from 'const';
import { dateString, useErrorHandler } from 'lib';

import { PackageEditDatesAdd_package$key } from '__generated__/PackageEditDatesAdd_package.graphql';
import { PackageEditDatesAddMutation } from '__generated__/PackageEditDatesAddMutation.graphql';

type Props = {
  package: PackageEditDatesAdd_package$key;
  connectionId: string;
};

type State = {
  startDate: Date | null;
  endDate: Date | null;
};

const canAdd = (startDate: Date | null, endDate: Date | null) =>
  startDate &&
  endDate &&
  format(startDate, DATE_FORMAT) !== format(endDate, DATE_FORMAT);

const PackageEditDatesAdd: FC<Props> = ({ connectionId, ...props }) => {
  const errorHandler = useErrorHandler();

  const [dates, setDates] = useState<State>({
    startDate: new Date(),
    endDate: new Date(),
  });

  const { id } = useFragment(
    graphql`
      fragment PackageEditDatesAdd_package on Package {
        id
      }
    `,
    props.package
  );

  const [add, adding] = useMutation<PackageEditDatesAddMutation>(graphql`
    mutation PackageEditDatesAddMutation(
      $input: CreatePackageDateInput!
      $connections: [ID!]!
    ) {
      addPackageDateToPackage(input: $input) {
        node
          @appendNode(
            connections: $connections
            edgeTypeName: "PackageDateEdge"
          ) {
          ...PackageDateRow_packageDate
        }

        package {
          ...PackageEditTransport_package
          ...PackageEditAccommodation_package
        }
      }
    }
  `);

  const { startDate, endDate } = dates;

  const handleAdd = useCallback(() => {
    if (!connectionId) return;

    add({
      variables: {
        connections: [connectionId],
        input: {
          packageId: id,
          start: dateString(startDate)!,
          end: dateString(endDate)!,
        },
      },

      ...errorHandler(),
    });
  }, [add, connectionId, endDate, errorHandler, id, startDate]);

  const handleChange = useCallback((item: RangeKeyDict) => {
    setDates({
      startDate: item.selection.startDate ?? null,
      endDate: item.selection.endDate ?? null,
    });
  }, []);

  return (
    <Box>
      <DateRangePickerBase
        ranges={[
          {
            startDate: startDate ?? undefined,
            endDate: endDate ?? undefined,
            key: 'selection',
          },
        ]}
        onChange={handleChange}
      />

      <Box sx={{ mt: 1 }}>
        <LoadingButton
          variant="contained"
          startIcon={<AddIcon />}
          loading={adding}
          disabled={!canAdd(startDate, endDate)}
          onClick={handleAdd}>
          Pridėti
        </LoadingButton>
      </Box>
    </Box>
  );
};

export default PackageEditDatesAdd;
